import type {
  Unsubscribe} from "firebase/firestore";
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { ref, computed, onUnmounted, watch } from "vue";
import { caseRequestConverter } from "~~/converters/case-request-converter";
import type { ICaseRequest} from "~~/types/case-request";
import { ECaseRequestStatus } from "~~/types/case-request";
import { EAccountType } from "~~/types/user";

export const useRequestNotifications = () => {
  const db = getFirestore();
  const userStore = useUserStore();
  const { startRepeatingSound, stopRepeatingSound } = useNotificationSound();

  // State
  const newRequests = ref<ICaseRequest[]>([]);
  const isListening = ref(false);
  const sessionStartTime = ref<Date>(new Date());
  const shownRequestIds = ref<Set<string>>(new Set());

  let unsubscribe: Unsubscribe | null = null;

  // Check if user is admin or owner
  const isAdmin = computed(() => {
    const user = userStore.currentUser;
    return user?.accountType === EAccountType.ADMIN || user?.accountType === EAccountType.OWNER;
  });

  // Get the first unshown request
  const currentNotification = computed(() => {
    const unshown = newRequests.value.find((req) => !shownRequestIds.value.has(req.id));
    return unshown || null;
  });

  // Check if there are more notifications to show
  const hasMoreNotifications = computed(() => {
    return newRequests.value.some((req) => !shownRequestIds.value.has(req.id));
  });

  // Count of unshown notifications
  const unshownCount = computed(() => newRequests.value.filter((r) => !shownRequestIds.value.has(r.id)).length);

  // Watch unshown count to manage repeating sound
  watch(unshownCount, (count) => {
    if (count > 0) {
      startRepeatingSound();
    } else {
      stopRepeatingSound();
    }
  });

  // Start listening for new requests
  const startListening = () => {
    if (!isAdmin.value) {
      console.log("Not an admin user, skipping request notifications");
      return;
    }

    if (isListening.value) {
      console.log("Already listening for request notifications");
      return;
    }

    // Set session start time to now
    sessionStartTime.value = new Date();

    try {
      // Query for requests created after session start
      const q = query(
        collection(db, "caseRequestsV2").withConverter(caseRequestConverter),
        where("status", "==", ECaseRequestStatus.CREATED),
        orderBy("timestamps.createdAt", "desc")
      );

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const request = change.doc.data() as ICaseRequest;

              // Only add if created after session start
              const createdAt = request.timestamps?.createdAt;
              if (createdAt) {
                const createdDate = createdAt instanceof Timestamp ? createdAt.toDate() : new Date(createdAt);

                if (createdDate > sessionStartTime.value) {
                  // Check if request is already in the list
                  const exists = newRequests.value.some((r) => r.id === request.id);
                  if (!exists) {
                    newRequests.value.unshift(request);
                  }
                }
              }
            } else if (change.type === "removed") {
              // Request no longer matches query (e.g., status changed from CREATED to ACCEPTED)
              // Remove from notification list and cleanup
              const requestId = change.doc.id;
              newRequests.value = newRequests.value.filter((r) => r.id !== requestId);
              shownRequestIds.value.delete(requestId);
            } else if (change.type === "modified") {
              // Handle explicit status changes
              const request = change.doc.data() as ICaseRequest;
              if (request.status !== ECaseRequestStatus.CREATED) {
                // Status changed to non-CREATED, remove from notifications
                newRequests.value = newRequests.value.filter((r) => r.id !== request.id);
                shownRequestIds.value.delete(request.id);
              }
            }
          });

          isListening.value = true;
        },
        (error) => {
          console.error("Error listening for new requests:", error);
          isListening.value = false;
        }
      );
    } catch (error) {
      console.error("Error setting up request notifications:", error);
      isListening.value = false;
    }
  };

  // Mark current notification as shown
  const markAsShown = (requestId: string) => {
    shownRequestIds.value.add(requestId);
  };

  // Dismiss current notification
  const dismissCurrent = () => {
    if (currentNotification.value) {
      markAsShown(currentNotification.value.id);
    }
  };

  // Stop listening
  const stopListening = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    stopRepeatingSound();
    isListening.value = false;
  };

  // Reset all state
  const reset = () => {
    stopListening();
    stopRepeatingSound();
    newRequests.value = [];
    shownRequestIds.value.clear();
    sessionStartTime.value = new Date();
  };

  // Auto cleanup on unmount
  onUnmounted(() => {
    stopListening();
  });

  return {
    // State
    isListening,
    currentNotification,
    hasMoreNotifications,
    isAdmin,
    newRequestsCount: computed(() => newRequests.value.length),
    unshownCount,

    // Methods
    startListening,
    stopListening,
    dismissCurrent,
    markAsShown,
    reset,
  };
};
